import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import UpdateIcon from "@mui/icons-material/Update";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import api from "../api/client";

const DISMISSED_KEY = "dismissedAnnouncementId";

const typeConfig = {
  update: { icon: <UpdateIcon />, color: "primary", label: "Update" },
  patch: { icon: <NewReleasesIcon />, color: "success", label: "Patch Notes" },
  alert: { icon: <WarningIcon />, color: "warning", label: "Alert" },
  info: { icon: <InfoIcon />, color: "info", label: "Information" },
};

export default function AnnouncementModal() {
  const [announcement, setAnnouncement] = useState(null);
  const [open, setOpen] = useState(false);

  const fetchLatestAnnouncement = useCallback(async () => {
    try {
      const response = await api.get("/announcements/latest");
      const data = response.data;
      
      if (data && data.id) {
        // Check if this announcement was already dismissed
        const dismissedId = localStorage.getItem(DISMISSED_KEY);
        if (dismissedId !== String(data.id)) {
          setAnnouncement(data);
          setOpen(true);
        }
      }
    } catch (error) {
      // Silent fail - announcements are not critical
      console.error("Failed to fetch announcement:", error);
    }
  }, []);

  useEffect(() => {
    // Only show on desktop (screen width > 768px)
    const isDesktop = window.innerWidth > 768;
    if (isDesktop) {
      // Small delay to let the app load first
      const timer = setTimeout(fetchLatestAnnouncement, 1500);
      return () => clearTimeout(timer);
    }
  }, [fetchLatestAnnouncement]);

  const handleDismiss = () => {
    if (announcement?.id) {
      localStorage.setItem(DISMISSED_KEY, String(announcement.id));
    }
    setOpen(false);
  };

  if (!announcement) return null;

  const config = typeConfig[announcement.type] || typeConfig.info;
  const formattedDate = new Date(announcement.createdAt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <Dialog
      open={open}
      onClose={handleDismiss}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)"
              : "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
          color: "white",
          p: 2,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={2} alignItems="center">
            {config.icon}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={config.label}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    color: "white",
                    fontWeight: 600,
                  }}
                />
                {announcement.version && (
                  <Chip
                    label={`v${announcement.version}`}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.2)",
                      color: "white",
                    }}
                  />
                )}
              </Stack>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {formattedDate}
              </Typography>
            </Box>
          </Stack>
          <IconButton size="small" onClick={handleDismiss} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {announcement.title}
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent>
        <Typography
          variant="body1"
          sx={{
            whiteSpace: "pre-wrap",
            lineHeight: 1.8,
          }}
        >
          {announcement.content}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={handleDismiss} variant="contained" fullWidth>
          Got it!
        </Button>
      </DialogActions>
    </Dialog>
  );
}
